import * as React from 'react';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject, ComparisonRangeType} from 'nutshell-core/types';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {DownloadIcon} from 'shells/icon/svg/download-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {HtmlLinkIcon} from 'shells/icon/svg/html-link-icon';
import {InvoiceIcon} from 'shells/icon/svg/invoice-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
import {PaginatedTable} from 'shells/table/paginated-table';
import {convertUrlSortParamToTableSortBy} from 'shells/table/table-sort-helpers';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {useCreateInvoiceFromQuote} from '../../../lead/sales-documents/quotes/graphql/hooks';
import {downloadQuote} from '../../../lead/sales-documents/shared';
import {useRestoreSalesDocument} from '../../../lead/sales-documents/shared/graphql/hooks';
import {QuotesReportChart} from '../../../reports/quotes/quotes-report-chart';
import {getReportDateRanges} from '../../../reports/report-constants';
import {ReportDateRangePicker} from '../../../reports/report-date-range-picker';
import {QUOTES} from '../../../reports/report-types';

import {appHistory} from '../../../../history';

import {
    getReportFiltersFromUrlParams,
    getReportParamsFromUrlParams,
    getTableColumns,
} from '../shared/helper';
import {QuoteFilters} from '../shared/quote-filters';
import {SalesDocumentGraphCard} from '../shared/sales-document-graph-card';
import {SalesDocumentOverviewEmptyState} from '../shared/sales-document-overview-empty-state';

import {useGetFilteredQuotes} from './graphql/hooks';

import styles from './quote-manage-page.module.css';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quote-script-container';

const SortOrder = {
    MODIFIED_TIME_DESC: '-modifiedTime',
    MODIFIED_TIME_ASC: 'modifiedTime',
    VALUE_ASC: 'value',
    VALUE_DESC: '-value',
} as const;

export function QuoteManagePage() {
    const urlParamString = window.location.search.slice(1); // Remove "?"
    const urlParams = Routing.deparam(urlParamString);
    const sort = urlParams.quotes ? urlParams.quotes.sort : null;

    const [activeFilters, setActiveFilters] = React.useState<FilterObject[]>([
        {
            owner: [],
            status: null,
        },
    ]);

    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);
    const instanceIdRef = React.useRef<string | null | undefined>(null);

    const {sessionInstance} = useGetInstanceInfo();
    const {user} = useGetSessionUser();
    const {restoreSalesDocument} = useRestoreSalesDocument();
    const {
        quotes,
        isLoading,
        error,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        numQuotes,
        fetchFirstPageWithNewSort,
    } = useGetFilteredQuotes({
        // @ts-expect-error
        userIds: activeFilters[0].owner || [],
        // @ts-expect-error
        status: activeFilters[0].status,
        sort,
    });
    const {createInvoiceFromQuote} = useCreateInvoiceFromQuote();

    const hasAnyFilters = Boolean(
        // @ts-expect-error
        activeFilters[0].status || (activeFilters[0].owner && activeFilters[0].owner.label)
    );

    const handleQuoteRestore = (quoteId: string) => {
        restoreSalesDocument({
            salesDocumentId: quoteId,
        }).then((response) => {
            refetch();
            if (
                response.data &&
                response.data.salesDocumentRestore &&
                response.data.salesDocumentRestore.salesDocument.modalUrl
            ) {
                appHistory.replace({
                    ...appHistory.location,
                    hash: response.data.salesDocumentRestore.salesDocument.modalUrl,
                    state: {modal: true},
                });
            }
        });
    };

    React.useEffect(() => {
        const handleQuoteCreated = () => {
            refetch();
        };

        window.addEventListener('salesDocumentCreated', handleQuoteCreated);

        return () => {
            window.removeEventListener('salesDocumentCreated', handleQuoteCreated);
        };
    }, [refetch]);

    React.useEffect(() => {
        instanceIdRef.current = sessionInstance && sessionInstance.instanceId;
    }, [sessionInstance]);

    const handleQuoteDownload = (slug: string) => {
        if (!instanceIdRef.current || !scriptRef.current) {
            return;
        }

        downloadQuote(
            instanceIdRef.current,
            slug,
            scriptRef.current,
            NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID
        );
    };

    const handleCreateInvoiceFromQuote = (quoteId: string) => {
        createInvoiceFromQuote({
            quote: quoteId,
        }).then((response) => {
            const invoiceRes =
                response &&
                response.data &&
                response.data.invoiceCreateFromQuote &&
                response.data.invoiceCreateFromQuote.invoice;
            if (invoiceRes) {
                appHistory.replace({
                    ...appHistory.location,
                    hash: invoiceRes.modalUrl,
                    state: {modal: true},
                });
            }
        });
    };

    const columns = React.useMemo(() => getTableColumns('Quote'), []);

    const onParamChange = React.useCallback(
        (params: any, replace?: boolean) => {
            const newUrlParams = {...urlParams, ...params} as const;
            const newParamString = Routing.param(newUrlParams);
            const newUrl = `${window.location.pathname}?${newParamString}${window.location.hash}`;

            if (replace) {
                appHistory.replace(newUrl);
            } else {
                appHistory.push(newUrl);
            }
        },
        [urlParams]
    );

    React.useEffect(() => {
        if (!sort) {
            // Necessary to keep sort state across page reloads
            // For some reason the second render does not have our sort param in urlParams
            // so we need to set it back
            onParamChange({quotes: {sort: SortOrder.MODIFIED_TIME_DESC}}, true);
        }
        if (!sort) {
            // If no sort is provided, default to MODIFIED_TIME_DESC
            onParamChange({quotes: {sort: SortOrder.MODIFIED_TIME_DESC}}, true);
        }
    }, [sort, onParamChange]);

    const updateUrlParamsOnSortChange = (newSortString: string) => {
        onParamChange({quotes: {sort: newSortString}}, true);
    };

    const quoteRowActionMenu = (row: any) => {
        if (!row || !row.original) {
            return null;
        }
        const isArchived = row.original.displayStatus === 'ARCHIVED';
        const isRevoked = row.original.displayStatus === 'REVOKED';
        const isAccepted = row.original.displayStatus === 'ACCEPTED';

        const downloadOption = {
            label: 'Download quote',
            icon: (
                <div className='pt-4'>
                    <DownloadIcon size={18} />
                </div>
            ),
            handleSelect: () => handleQuoteDownload(row.original.slug),
        } as const;

        const options = [];
        if (!isArchived && !isRevoked) {
            options.push(
                {
                    label: 'View quote',
                    icon: (
                        <div className='pt-4'>
                            <EyeIcon size={18} />
                        </div>
                    ),
                    handleSelect: () =>
                        window.open(row.original.shareableViewInBrowserLink, '_blank'),
                },
                {
                    label: 'Copy link to quote',
                    icon: <HtmlLinkIcon size={18} />,
                    handleSelect: () =>
                        navigator.clipboard.writeText(row.original.shareableViewInBrowserLink),
                },
                downloadOption
            );
            if (isAccepted) {
                options.push({
                    label: 'Create an invoice',
                    icon: <InvoiceIcon size={18} />,
                    handleSelect: () => handleCreateInvoiceFromQuote(row.original.id),
                });
            }
        } else if (isArchived) {
            options.push(downloadOption, {
                label: 'Restore quote',
                icon: (
                    <div className='pt-4'>
                        <RetryRegularIcon size={18} />
                    </div>
                ),
                handleSelect: () => handleQuoteRestore(row.original.id),
            });
        } else if (isRevoked) {
            options.push(downloadOption);
        }

        return (
            <div className='px-4'>
                <ActionMenu padding='less' hasHoverBackground={true} options={options} />
            </div>
        );
    };

    const onParamPush = (params: any, replace?: boolean) => {
        onParamChange(params, replace);
    };

    const reportParams = React.useMemo(() => getReportParamsFromUrlParams(urlParams), [urlParams]);

    const reportFilters = React.useMemo(
        () => getReportFiltersFromUrlParams(urlParams),
        [urlParams]
    );

    const handleDateRangeChange = (
        newDateFilter: FilterObject,
        comparisonType?: ComparisonRangeType
    ) => {
        const newParams = {filter: newDateFilter, report: {compareTo: comparisonType}} as const;
        onParamChange(newParams, true);
    };

    return (
        <div className='full-height flex flex-dir-col'>
            {NutClientConfig.hasSalesDocumentGraphsFF && (
                <>
                    <div className='flex justify-end pt-16 pr-16'>
                        <ReportDateRangePicker
                            activeFilters={reportFilters}
                            dateRangeType='period'
                            // @ts-expect-error
                            dateRangeOptions={getReportDateRanges(QUOTES)}
                            comparisonType={undefined}
                            onDateRangeChange={handleDateRangeChange}
                            hideCompareTo={true}
                        />
                    </div>
                    <SalesDocumentGraphCard
                        salesDocumentType='quote'
                        chart={
                            <QuotesReportChart
                                salesDocumentType='quote'
                                activeFilters={reportFilters}
                                onParamChange={onParamChange}
                                onParamPush={onParamPush}
                                reportParams={reportParams}
                            />
                        }
                    />
                </>
            )}
            <div className={styles.table}>
                <ListPageControlBar
                    leftSection={
                        <QuoteFilters
                            activeFilters={activeFilters}
                            onUpdateFilters={(filter: FilterObject) => {
                                setActiveFilters([filter]);
                            }}
                            isAdmin={user && user.permissions.canAccessBilling}
                        />
                    }
                />
                <PaginatedTable
                    key={`quotes-${JSON.stringify(activeFilters)}`}
                    columns={columns}
                    data={quotes || []}
                    isLoading={isLoading}
                    fullWidth={true}
                    fetchNextPage={fetchNextPage}
                    fetchPreviousPage={fetchPreviousPage}
                    pageSize={50}
                    sortBy={sort ? convertUrlSortParamToTableSortBy(sort) : undefined}
                    updateUrlParamsOnSortChange={updateUrlParamsOnSortChange}
                    fetchFirstPageWithNewSort={fetchFirstPageWithNewSort}
                    totalCount={numQuotes}
                    emptyState={
                        <SalesDocumentOverviewEmptyState
                            hasAnyFilters={hasAnyFilters}
                            type='QUOTE'
                        />
                    }
                    error={error || undefined}
                    // @ts-expect-error
                    renderRowActionMenu={quoteRowActionMenu}
                />
            </div>
            <div className={styles['quote-script-container']}>
                {/* @ts-expect-error */}
                <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
            </div>
        </div>
    );
}
